import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { Header, Grid, Button, Form, InputOnChangeData, Label, Popup, Input } from "semantic-ui-react";

import MaskedInput from "../../components/MaskedInput";

import { eStatusKit, IConferenceSectorManager, IKit } from "../../interfaces";
import ConferenceRepository from "../../Repositories/ConferenceRepository";
import KitRepository, { IKitListModel } from "../../Repositories/KitRepository";

import NumberFormat from "../../Utils/NumberFormat";
import errors from "../../Utils/ExtractErrors";
import { RouteComponentProps, Redirect } from "react-router-dom";
import ModalYesNo from "../../components/ModalYesNo";
import TemplateMain from "../../components/TemplateMain";
import TableApp, { ITableAppRender } from "../../components/TableApp";

interface IProps {
    id: string;
}

interface ILocationState {
    gerenteSetor: string;
    month: number;
    status: string;
}

interface IKitModel extends IKit {
    selected: boolean;
}

interface IKitListUpdateModel extends IKitListModel {
    positive?: boolean;
    selected: boolean;
}

const ConferenceSector: React.FC<RouteComponentProps<IProps, any, ILocationState>> = ({ match, location }) => {

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [valorRecebido, setValorRecebido] = useState<string>("");
    const [numero, setNumero] = useState<string>("");

    const [items, setItems] = useState<IKitListUpdateModel[]>([]);
    const [conferences, setConferences] = useState<IConferenceSectorManager[]>([]);
    const [kits, setKits] = useState<IKitModel[]>([]);
    const [redirect, setRedirect] = useState<boolean>(false);

    const load = useCallback(async () => {

        setLoading(true);
        try {
            const response = await Promise.all([
                ConferenceRepository.GetConferencesBySectorManagerId(match.params.id),
                KitRepository.ListConferenceToUpdate({
                    filter: location.state.status,
                    gerentesetorid: match.params.id
                }),
                KitRepository.List({
                    filter: `PEDIDO.GERENTESETORID == "${match.params.id}" && STATUSKIT == 6`
                })
            ]);

            setConferences(response[0]);
            setKits(response[1] as IKitModel[]);
            setItems(response[2].list as IKitListUpdateModel[]);

        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setLoading(false);
    }, [match.params.id, location.state.status]);

    const handleOnChangeValorRecebido = (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setValorRecebido(data.value);
    }

    const handleFinalizar = async () => {
        setSaving(true);
        try {
            const response = await ConferenceRepository.CreateConferenceRegionalManager({
                sectorId: match.params.id,
                valor: handleValorReceber(),
                kits: kits.filter(kit => kit.selected)
            });

            setRedirect(true);

            toast(response.message, { position: "top-center", type: "success" });
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
        setSaving(false);
    }

    const handleValorReceber = () => {
        return conferences.reduce((accumulator: number, item: IConferenceSectorManager) => {
            return accumulator += item.valorRecebido;
        }, 0);
    }

    const setSelected = (selected: boolean, numero: number) => {
        if (selected) {
            return <Label icon="check" color="green" size="large" content={numero} />
        } else {
            return <Label icon="times" size="large" content={numero} />
        }
    }

    const setEstado = (estado: number, gerenteSetor: string) => {
        switch (estado) {
            case 1:
                return <Popup
                    trigger={<Label ribbon icon="user times" color="red" content="DEV" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 2:
                return <Popup
                    trigger={<Label ribbon icon="thumbs down" color="black" content="NRT" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
        }
    }

    const setStatus = (status: number, gerenteSetor: string) => {
        switch (status) {
            case 0:
                return <Label ribbon icon="box" content="EST" />
            case 1:
                return <Popup
                    trigger={<Label ribbon icon="user" color="pink" content="GES" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 2:
                return <Popup
                    trigger={<Label ribbon icon="id badge" color="violet" content="REP" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 3:
                return <Popup
                    trigger={<Label ribbon icon="street view" color="teal" content="REV" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 4:
                return <Popup
                    trigger={<Label ribbon icon="stopwatch" color="blue" content="PRE" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 5:
                return <Popup
                    trigger={<Label ribbon icon="thumbs down outline" color="orange" content="CFE" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 6:
                return <Popup
                    trigger={<Label ribbon icon="thumbs up outline" color="purple" content="CGS" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 7:
                return <Popup
                    trigger={<Label ribbon icon="handshake" color="olive" content="CGR" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            case 8:
                return <Popup
                    trigger={<Label ribbon icon="check" color="green" content="FIN" />}
                    content={`G. Setor: ${gerenteSetor !== null ? gerenteSetor : "Indefinido"}`}
                    wide
                />
            default:
                return <Label ribbon icon="dont" color="grey" content="???" />
        }
    }

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        setNumero(data.value);
    }

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter")
            handleAddItem();
    }

    const handleAddItem = () => {
        const item = kits.find(kit => kit.numero === Number(numero));
        const itemKit = items.find(kit => kit.numero === Number(numero));

        if (!!item && !!itemKit) {
            setNumero("");

            const restKits = kits.filter(kit => kit.numero !== Number(numero));
            const restItems = items.filter(kit => kit.numero !== Number(numero));

            item.selected = true;

            itemKit.selected = true;
            itemKit.positive = true;

            setItems([...restItems, itemKit]);
            setKits([...restKits, item]);
        }
        else {
            toast("Kit não encontrado", { position: "top-center", type: "error" });
        }
    }

    const handleRemoveItem = (numero: number) => {
        const item = kits.find(kit => kit.numero === Number(numero));
        const itemKit = items.find(kit => kit.numero === Number(numero));

        if (!!item && !!itemKit) {
            const restKits = kits.filter(kit => kit.numero !== Number(numero));
            const restItems = items.filter(kit => kit.numero !== Number(numero));

            item.selected = false;

            itemKit.selected = false;
            itemKit.positive = false;

            setItems([itemKit, ...restItems]);
            setKits([item, ...restKits]);
        }
        else {
            toast("Kit não encontrado", { position: "top-center", type: "error" });
        }
    }

    const removeButton = (numero: number) => {
        return <Button icon="undo alternate" color="red" onClick={() => handleRemoveItem(numero)} />;
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Status",
            content: (x: IKitListUpdateModel) => x.estado !== 0 ? setEstado(x.estado, x.gerenteSetor) : setStatus(x.status, x.gerenteSetor),
            textAlign: "left",
            width: 1
        },
        {
            header: "Número",
            content: (x: IKitListUpdateModel) => setSelected(x.selected, x.numero),
            textAlign: "center",
            width: 2
        },
        {
            header: "Revendedor",
            content: (x: IKitListUpdateModel) => x.revendedor,
            width: 4
        }
    ];

    useEffect(() => { load(); }, [load]);

    const content = (
        <Grid columns="equal">
            <Grid.Row>
                <Grid.Column>
                    <Header size="huge" content={`Conferência`} icon="calendar check outline" />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column>
                    <Form>
                        <Form.Group widths="equal">
                            <Form.Field>
                                <Form.Input
                                    fluid
                                    readOnly
                                    label="Gerente setor"
                                    placeholder="Indefinido"
                                    value={location.state.gerenteSetor}
                                />
                            </Form.Field>
                        </Form.Group>
                    </Form>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column>
                    <ModalYesNo
                        trigger={
                            <Button
                                fluid
                                icon="check"
                                color="pink"
                                content="Finalizar"
                                loading={saving}
                            />}
                        header="Finalizar conferência"
                        content={`Confirma a conferência do gerente de setor ${location.state.gerenteSetor}?`}
                        onSuccess={handleFinalizar}
                    />
                </Grid.Column>
            </Grid.Row>
            <Grid.Row>
                <Grid.Column>
                    <Input
                        fluid
                        label="Nº do kit"
                        placeholder="Insira o nº do kit"
                        onChange={handleOnChange}
                        onKeyDown={handleOnKeyDown}
                        value={numero}
                        action={{ icon: "add", color: "pink", onClick: handleAddItem }}
                    />
                </Grid.Column>
            </Grid.Row>
            <Grid.Column>
                <Grid.Row>
                    <TableApp
                        render={tableRender}
                        list={items}
                        loading={false}
                        actions={(data: IKitListUpdateModel, index: number) => {
                            if (data.selected) {
                                return [removeButton(data.numero)]
                            }
                        }}
                    />
                </Grid.Row>
            </Grid.Column>
        </Grid>
    );

    return (
        <>
            {redirect && <Redirect to={{
                pathname: `/conferencesectors`,
                state: {
                    month: location.state.month,
                    status: location.state.status
                }
            }}
            />}

            {!redirect &&
                <TemplateMain
                    loading={loading}
                    content={content}
                />}
        </>
    );
};

export default ConferenceSector;
